import React from "react";

import { faFile, faTriangleExclamation } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { MemoizedMarkdown } from "../../../components/Markdown";
import { SourcesBox } from "./SourcesBox";

import type { ChatMessage as ChatMessageType } from "../types";

import { useAskPerformYardStore } from "../AskPerformYardStore";

type ChatMessageProps = {
	message: ChatMessageType;
};

export const ChatMessage: React.FC<ChatMessageProps> = ({ message }) => {
	const openDocumentModal = useAskPerformYardStore((state) => state.openDocumentModal);
	const isAssistant = message.role === "assistant";
	const isUser = message.role === "user";

	const sources = message.sources ?? [];
	// Show sources if they exist (backend handles filtering based on confidence)
	const shouldShowSources = sources.length > 0;
	const hasSingleSource = shouldShowSources && sources.length === 1;
	const hasMultipleSources = shouldShowSources && sources.length >= 2;

	return (
		<div
			className={`box-border flex duration-200 animate-in fade-in slide-in-from-bottom-2 ${
				isUser ? "ml-auto w-[92%] flex-col items-end py-2" : "w-full flex-col items-start py-2"
			}`}
		>
			{isAssistant && (
				<div className="flex max-w-full items-end">
					{/* Avatar */}
					<img src="/static/img/py-logo/py-round-icon.svg" alt="PerformYard" className="h-6 w-6 shrink-0" />

					{/* Message content */}
					<div className="box-border flex min-w-0 flex-col gap-2 overflow-hidden pl-2">
						<div className="overflow-hidden rounded-lg rounded-bl-none bg-[#F5F5F5] px-2 py-2">
							{message.isStreaming && !message.content ? (
								<p className="text-sm text-neutral-600">Thinking...</p>
							) : (
								<div className="break-words" aria-live={message.isStreaming ? "polite" : "off"}>
									<MemoizedMarkdown content={message.content} id={message.id} />
									{message.isStreaming && (
										<span className="ml-0.5 inline-block h-4 w-1.5 animate-pulse rounded-sm bg-neutral-400 align-middle" />
									)}
								</div>
							)}

							{/* Single source - inline display */}
							{hasSingleSource && (
								<div className="mt-3">
									<button
										type="button"
										onClick={() => openDocumentModal(sources[0].s3FileId, sources[0].filename)}
										className="inline-flex max-w-full cursor-pointer items-center gap-2 border-none bg-transparent p-0 text-left text-sm text-primary-5 hover:!text-primary-7 hover:underline"
									>
										<FontAwesomeIcon icon={faFile} className="h-4 w-4 shrink-0" />
										<span className="truncate font-medium">{sources[0].filename}</span>
									</button>
								</div>
							)}

							{/* Multiple sources - collapsible box */}
							{hasMultipleSources && <SourcesBox sources={sources} onSourceClick={openDocumentModal} />}

							{/* Subtle indicator when response may be incomplete */}
							{message.isIncomplete && (
								<div className="mt-2 flex items-center gap-1.5 border-t border-amber-200 pt-2 text-xs text-amber-600">
									<FontAwesomeIcon icon={faTriangleExclamation} className="h-3 w-3 shrink-0" />
									<span>This response may be incomplete</span>
								</div>
							)}
						</div>
					</div>
				</div>
			)}

			{isUser && (
				<div className="rounded-lg rounded-br-none bg-[#3E90C1] px-2 py-2">
					<p className="whitespace-pre-wrap break-words text-sm leading-[16px] text-white">
						{message.content}
					</p>
				</div>
			)}
		</div>
	);
};

export default ChatMessage;
